import { MutableRefObject, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { heapEvent } from '@equipmentshare/heaper'
import { useCompleteDemandRequestLineItemsMutation, useBulkUpdateDemandRequestLineItemFulfillerNotesMutation } from '../../../graphql'
import { useDispatchSetSelectedModal } from '../../../contexts/modal-context/modal-context'
import { getCreateTransferFromRequestModal } from '../components/actionModals'
import { LineItem } from '../../../types/demandRequest'


type Inventory = {
	id: string,
}

type ReceivingInventory = {
	id: string,
	branchId?: string | null,
}

type DemandRequest = {
	id: string,
	requestNumber: number,
	lineItems: LineItem[],
	// Transfers leave from the owning inventory: the fulfilling inventory, or the
	// main DC when a routed stock request has none
	owningInventory: Inventory,
	requestingInventory: ReceivingInventory,
}

type UseTransferCreationParams = {
	request: DemandRequest | undefined | null,
	selectedLineItemIds: string[],
	useNewFlowRef: MutableRefObject<boolean>,
	addToast: ( message: string ) => void,
	clearSelection: () => void,
}

/**
 * Hook for creating inventory transfers from selected line items
 */
export function useTransferCreation( {
	request,
	selectedLineItemIds,
	useNewFlowRef,
	addToast,
	clearSelection,
}: UseTransferCreationParams ) {
	const [ completeLineItems ] = useCompleteDemandRequestLineItemsMutation()
	const [ bulkUpdateFulfillerNotes ] = useBulkUpdateDemandRequestLineItemFulfillerNotesMutation()
	const setSelectedModal = useDispatchSetSelectedModal()
	const navigate = useNavigate()

	const handleTransferSuccess = useCallback( async( fullyFulfilledLineItemIds: string[], transactionId?: number ) => {
		if ( !request ) {
			return
		}

		// First, add fulfiller notes with transaction ID for all selected items
		if ( transactionId && selectedLineItemIds.length > 0 ) {
			try {
				const result = await bulkUpdateFulfillerNotes( {
					variables: {
						input: {
							demandRequestId: request.id,
							lineItemIds: selectedLineItemIds,
							fulfillerNotes: `TX-${transactionId}`,
						},
					},
				} )

				if ( result.data?.bulkUpdateDemandRequestLineItemFulfillerNotes?.success ) {
					heapEvent( 'Inventory - Stock Request - Transfer Fulfiller Notes Added', {
						demand_request_id: request.id,
						transaction_id: transactionId,
						item_count: selectedLineItemIds.length,
					} )
				}
			} catch ( err ) {
				// Silently fail - the transfer was successful, just the notes update failed
				// eslint-disable-next-line no-console
				console.error( 'Failed to update fulfiller notes:', err )
			}
		}

		// Then, auto-complete fully fulfilled items
		if ( fullyFulfilledLineItemIds.length > 0 ) {
			try {
				const result = await completeLineItems( {
					variables: {
						input: {
							demandRequestId: request.id,
							lineItemIds: fullyFulfilledLineItemIds,
						},
					},
				} )

				if ( result.data?.completeDemandRequestLineItems?.success ) {
					addToast( `${fullyFulfilledLineItemIds.length} fully-fulfilled item(s) automatically marked as completed` )
					clearSelection()

					heapEvent( 'Inventory - Stock Request - Transfer Auto Complete', {
						demand_request_id: request.id,
						item_count: fullyFulfilledLineItemIds.length,
					} )
				}
			} catch ( err ) {
				// Silently fail - the transfer was successful, just the auto-complete failed
				// eslint-disable-next-line no-console
				console.error( 'Failed to auto-complete line items:', err )
			}
		} else {
			// If no items were fully fulfilled, still clear the selection after adding notes
			clearSelection()
		}
	}, [ request, selectedLineItemIds, completeLineItems, bulkUpdateFulfillerNotes, addToast, clearSelection ] )

	const handleCreateTransfer = useCallback( () => {
		if ( !request || selectedLineItemIds.length === 0 ) {
			return
		}

		// Map IDs → items in selectedLineItemIds order (display order) rather than
		// filtering request.lineItems, which would revert to server insertion order.
		const selectedLineItems = selectedLineItemIds
			.map( ( id ) => request.lineItems.find( ( lineItem ) => lineItem.lineItemId === id ) )
			.filter( ( item ): item is LineItem => item !== undefined )

		if ( useNewFlowRef.current ) {
			heapEvent( 'Inventory - Stock Request - Bulk Actions - Create Transfer', {
				demand_request_id: request.id,
				item_count: selectedLineItemIds.length,
			} )
			navigate( '/transfer-orders/new', {
				state: {
					fromInventoryId: parseInt( request.owningInventory.id, 10 ),
					toInventoryId: parseInt( request.requestingInventory.id, 10 ),
					selectedLineItems,
					requestId: request.id,
					requestNumber: request.requestNumber,
				},
			} )
		} else {
			// Open modal with data
			setSelectedModal( getCreateTransferFromRequestModal( {
				fromInventoryId: parseInt( request.owningInventory.id, 10 ),
				toInventoryId: parseInt( request.requestingInventory.id, 10 ),
				selectedLineItems,
				requestId: request.id,
				requestNumber: request.requestNumber,
				onTransferSuccess: handleTransferSuccess,
			} ) )

			heapEvent( 'Inventory - Stock Request - Bulk Actions - Create Transfer', {
				demand_request_id: request.id,
				item_count: selectedLineItemIds.length,
			} )
		}
	}, [ request, selectedLineItemIds, navigate, handleTransferSuccess, setSelectedModal ] )

	return {
		handleCreateTransfer,
	}
}
